import { useEffect, useState } from 'react';

export type ScreenOrientation = 'portrait' | 'landscape';
export type ScreenTier = 'mobile' | 'compactTablet' | 'standardTablet';

export interface ResponsiveScreen {
  width: number;
  height: number;
  shortSide: number; // min(width, height)
  orientation: ScreenOrientation;
  tier: ScreenTier;
}

// Sensible defaults for SSR / non-browser environments (matches a mid tablet).
const DEFAULT_WIDTH = 1024;
const DEFAULT_HEIGHT = 768;

// Tier boundaries, keyed off the viewport short side so a device lands in the
// same tier in both orientations:
//   mobile          short side < 500   (e.g. iPhone 12 - 390)
//   compactTablet   short side 500-639 (e.g. 7" Android - 600)
//   standardTablet  short side >= 640  (e.g. Galaxy Tab A11+ - 686, iPad Mini - 768)
export const COMPACT_TABLET_MIN = 500;
export const STANDARD_TABLET_MIN = 640;

export function getTier(shortSide: number): ScreenTier {
  if (shortSide < COMPACT_TABLET_MIN) {
    return 'mobile';
  }
  if (shortSide < STANDARD_TABLET_MIN) {
    return 'compactTablet';
  }
  return 'standardTablet';
}

// Portrait has spare vertical space (the QR sits in a tall column), so the code
// is rendered 20% larger there than in landscape, where height is the
// constraint. Applied only when an orientation is passed in.
export const PORTRAIT_QR_SCALE = 1.2;

// Continuous, clamped-per-tier QR size. The QR scales as a fraction of the
// viewport short side, bounded so it never gets uselessly small or overflows.
export function getQrSize(
  tier: ScreenTier,
  shortSide: number,
  orientation?: ScreenOrientation,
): number {
  const ratio = 0.52;
  const caps = {
    mobile: { min: 168, max: 240 },
    compactTablet: { min: 288, max: 384 },
    standardTablet: { min: 360, max: 520 },
  }[tier];
  const scale = orientation === 'portrait' ? PORTRAIT_QR_SCALE : 1;

  return Math.round(
    Math.min(caps.max, Math.max(caps.min, shortSide * ratio)) * scale,
  );
}

// A QR smaller than this stops being reliably scannable at kiosk arm's length.
// The fit below may go under getQrSize's per-tier minimum, but never under this.
export const MIN_SCANNABLE_QR = 200;

// Fixed chrome around the landscape QR row that the measured header/footer
// heights don't cover: the `qr` wrapper's margins (8 top + 8 bottom), the
// footer's marginTop (16, a margin outside its border box) and 8px of rounding
// safety.
export const LANDSCAPE_QR_GUTTER = 40;

// Landscape stacks title/countdown, QR row and subtext in a single column, and
// every font size there is driven by vmin (= the viewport height). That makes
// the text sizes blind to how much *width* the merchant-authored title and
// subtitle have to wrap into: two devices with the same height but different
// widths (7" 1024x600 vs 8" 962x601) get identical sizes yet different line
// counts, so the column's intrinsic height changes while the QR's does not.
// Give the QR the space the text actually left over instead of letting the
// column overflow the viewport - it only ever shrinks, so devices with room to
// spare keep the exact size getQrSize asked for.
export function getFittedQrSize({
  desiredSize,
  viewportHeight,
  reservedHeight,
  minSize = MIN_SCANNABLE_QR,
}: {
  desiredSize: number;
  viewportHeight: number;
  reservedHeight: number;
  minSize?: number;
}): number {
  const available = Math.floor(viewportHeight - reservedHeight);

  // Ordered so `desiredSize` is the hard ceiling: the floor stops the QR being
  // shrunk into unscannability, but it must never *grow* one past the size its
  // tier asked for - on the mobile tier `desiredSize` can itself be under
  // `minSize` (e.g. 195 on a 375px-tall landscape phone).
  return Math.min(desiredSize, Math.max(minSize, available));
}

function getScreen(): ResponsiveScreen {
  if (typeof window === 'undefined') {
    const shortSide = Math.min(DEFAULT_WIDTH, DEFAULT_HEIGHT);
    return {
      width: DEFAULT_WIDTH,
      height: DEFAULT_HEIGHT,
      shortSide,
      orientation: 'portrait',
      tier: getTier(shortSide),
    };
  }

  const width = window.innerWidth || DEFAULT_WIDTH;
  const height = window.innerHeight || DEFAULT_HEIGHT;
  const shortSide = Math.min(width, height);

  const isPortrait =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(orientation: portrait)').matches
      : height >= width;

  return {
    width,
    height,
    shortSide,
    orientation: isPortrait ? 'portrait' : 'landscape',
    tier: getTier(shortSide),
  };
}

// Tracks live viewport size, orientation and layout tier. Re-renders on resize
// and orientation change so every dependent size stays proportionate.
export default function useResponsiveScreen(): ResponsiveScreen {
  const [screen, setScreen] = useState<ResponsiveScreen>(getScreen);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handler = () => setScreen(getScreen());
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);

    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  return screen;
}
